import React from "react";
import { Form, Input, Button, Flex, theme, type FormInstance, InputNumber, Select } from "antd";
import { CheckCircleFilled, TagOutlined, BarcodeOutlined, DollarOutlined, PercentageOutlined } from "@ant-design/icons";
import { SectionHeader, FieldLabel, inputStyle } from "../../shared";
import type { ProductData } from "../types";

interface ProductFormProps {
  form: FormInstance<ProductData>;
  onFinish: (values: ProductData) => void;
  isPending: boolean;
  buttonText: string;
  formId?: string;
}

const ProductForm: React.FC<ProductFormProps> = ({ form, onFinish, isPending, buttonText, formId = "product-form" }) => {
  const { token } = theme.useToken();
  const iStyle = inputStyle(token);

  return (
    <Form
      id={formId}
      form={form}
      layout="vertical"
      onFinish={onFinish}
      requiredMark={false}
      style={{ width: "100%" }}
    >
      <Flex vertical gap={16} style={{ marginBottom: 28 }}>
        <SectionHeader title="Información del Producto" />

        <Form.Item
          name="product_name"
          label={<FieldLabel text="Nombre del Producto" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<TagOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. Software de Facturación"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="product_description"
          label={<FieldLabel text="Descripción" />}
          style={{ marginBottom: 0 }}
        >
          <Input.TextArea
            size="large"
            rows={3}
            maxLength={500}
            showCount
            placeholder="Descripción opcional del producto o servicio"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="product_key_sat"
          label={<FieldLabel text="Clave SAT" />}
          style={{ marginBottom: 0 }}
        >
          <Input
            size="large"
            prefix={<BarcodeOutlined style={{ color: token.colorTextTertiary }} />}
            placeholder="Ej. 84111506"
            style={iStyle}
          />
        </Form.Item>

        <Form.Item
          name="product_price"
          label={<FieldLabel text="Precio Unitario" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            size="large"
            prefix={<DollarOutlined style={{ color: token.colorTextTertiary }} />}
            style={{ ...iStyle, width: '100%' }}
            min={0}
            step={0.01}
          />
        </Form.Item>
        <Form.Item
          name="product_price_sale"
          label={<FieldLabel text="Precio de Venta" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            size="large"
            prefix={<DollarOutlined style={{ color: token.colorTextTertiary }} />}
            style={{ ...iStyle, width: '100%' }}
            min={0}
            step={0.01}
          />
        </Form.Item>
        <Form.Item
          name="product_type"
          label={<FieldLabel text="Tipo de producto" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            size="large"
            prefix={<TagOutlined style={{ color: token.colorTextTertiary }} />}
            style={{ ...iStyle, width: '100%' }}
            options={[
              { label: "Servicio", value: "Servicio" },
              { label: "Producto", value: "Producto" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="product_iva"
          label={<FieldLabel text="Iva del producto" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            size="large"
            prefix={<DollarOutlined style={{ color: token.colorTextTertiary }} />}
            style={{ ...iStyle, width: '100%' }}
            min={0}
            step={0.01}
          />
        </Form.Item>

        <Form.Item
          name="product_iva_percentage"
          label={<FieldLabel text="Porcentaje del producto" />}
          rules={[{ required: true, message: "Campo requerido" }]}
          style={{ marginBottom: 0 }}
        >
          <InputNumber
            size="large"
            prefix={<PercentageOutlined style={{ color: token.colorTextTertiary }} />}
            style={{ ...iStyle, width: '100%' }}
            min={0}
            step={0.01}
          />
        </Form.Item>
      </Flex>

      <Button
        type="primary"
        htmlType="submit"
        size="large"
        loading={isPending}
        icon={<CheckCircleFilled />}
        block
        style={{
          height: 54,
          borderRadius: token.borderRadiusLG,
          fontWeight: 800,
          fontSize: 16,
          letterSpacing: "0.01em",
          boxShadow: `0 8px 20px ${token.colorPrimaryBg}`,
        }}
      >
        {buttonText}
      </Button>
    </Form>
  );
};

export default ProductForm;
